/**
 * 音效管理器
 * 负责短促音效的播放，支持多音效叠加
 */
import { AudioManager } from './audio-manager';
import type { AudioResource, PlayOptions, AudioConfigMap } from './types';
import { getPublicUrl } from '@/utils/cdn';

interface LoopingSound {
  audio: HTMLAudioElement;
  uniqueKey: string;
}

/**
 * 全局预加载状态管理
 * 记录已经预加载成功的路径，避免多个实例重复预加载
 */
const globalPreloadCache = {
  // 已预加载成功的路径集合
  loadedPaths: new Set<string>(),
  // 正在预加载中的路径及其 Promise
  loadingPromises: new Map<string, Promise<void>>(),
};

export class SoundEffectManager {
  private audioManager: AudioManager;
  private resources: Map<string, AudioResource> = new Map();
  private loopingSounds: Map<string, LoopingSound> = new Map();

  constructor(audioManager: AudioManager) {
    this.audioManager = audioManager;
  }

  /**
   * 注册音频资源
   */
  register(id: string, path: string): void {
    this.resources.set(id, {
      id,
      path: getPublicUrl(path),
      loaded: false,
    });
  }

  /**
   * 批量注册音频资源
   */
  registerAll(configMap: AudioConfigMap): void {
    Object.entries(configMap).forEach(([id, path]) => {
      this.register(id, path);
    });
  }

  /**
   * 检查资源是否已注册
   */
  isRegistered(id: string): boolean {
    return this.resources.has(id);
  }

  /**
   * 取消注册/卸载音频资源
   */
  unregister(id: string): void {
    const resource = this.resources.get(id);
    if (resource?.audioElement) {
      resource.audioElement.pause();
      resource.audioElement.src = '';
      resource.audioElement.load();
      resource.audioElement = undefined;
    }
    this.resources.delete(id);
  }

  /**
   * 预加载音频
   * 如果预加载失败，会在控制台记录警告但不抛出错误，避免未处理的 Promise rejection
   * 使用全局缓存避免多个实例重复预加载同一资源
   */
  async preload(id: string): Promise<void> {
    const resource = this.resources.get(id);
    if (!resource) {
      console.warn(`Sound effect resource "${id}" is not registered`);
      return;
    }

    // 检查实例级别的预加载状态
    if (resource.loaded && resource.audioElement) {
      return;
    }

    // 检查全局预加载状态
    const path = resource.path;

    // 如果全局已经预加载成功，直接返回
    if (globalPreloadCache.loadedPaths.has(path)) {
      // 如果实例还没有 audioElement，创建一个（但不重新加载）
      if (!resource.audioElement) {
        const audio = new Audio(path);
        audio.preload = 'auto';
        resource.audioElement = audio;
        resource.loaded = true;
      }
      return;
    }

    // 如果正在预加载中，等待现有的 Promise
    const existingPromise = globalPreloadCache.loadingPromises.get(path);
    if (existingPromise) {
      await existingPromise;
      // 等待完成后，再次检查全局状态并更新实例状态
      if (globalPreloadCache.loadedPaths.has(path) && !resource.audioElement) {
        const audio = new Audio(path);
        audio.preload = 'auto';
        resource.audioElement = audio;
        resource.loaded = true;
      }
      return;
    }

    // 创建新的预加载 Promise
    const preloadPromise = new Promise<void>((resolve) => {
      const audio = new Audio(path);
      audio.preload = 'auto';

      audio.addEventListener('canplaythrough', () => {
        // 更新全局缓存
        globalPreloadCache.loadedPaths.add(path);
        globalPreloadCache.loadingPromises.delete(path);

        // 更新实例状态
        resource.audioElement = audio;
        resource.loaded = true;
        resolve();
      });

      audio.addEventListener('error', (e) => {
        const errorMessage = (e.target as HTMLAudioElement).error?.message || 'Unknown error';
        console.warn(`Failed to preload sound effect "${id}": ${errorMessage}`);

        // 即使失败也要从 loadingPromises 中移除，避免永久阻塞
        globalPreloadCache.loadingPromises.delete(path);

        resolve(); // 静默处理错误，不抛出
      });

      audio.load();
    });

    // 记录正在预加载的 Promise
    globalPreloadCache.loadingPromises.set(path, preloadPromise);

    return preloadPromise;
  }

  /**
   * 批量预加载音频
   * @param ids 可选的资源 ID 数组，如果不提供则预加载所有已注册的资源
   * @returns Promise，包含所有预加载结果（成功和失败）
   */
  async preloadAll(ids?: string[]): Promise<Array<{ id: string; success: boolean; error?: Error; }>> {
    const targetIds = ids ?? Array.from(this.resources.keys());

    const preloadPromises = targetIds.map(async (id) => {
      const resource = this.resources.get(id);
      if (!resource) {
        return {
          id,
          success: false,
          error: new Error(`Sound effect resource "${id}" is not registered`),
        };
      }

      await this.preload(id);

      // 检查是否真的加载成功
      if (resource.loaded && resource.audioElement) {
        return { id, success: true };
      } else {
        return {
          id,
          success: false,
          error: new Error(`Failed to preload sound effect "${id}"`),
        };
      }
    });

    return Promise.all(preloadPromises);
  }

  /**
   * 获取或创建音频元素（每次播放都创建新的实例以支持叠加）
   */
  private createAudioElement(id: string): HTMLAudioElement {
    const resource = this.resources.get(id);
    if (!resource) {
      throw new Error(`Sound effect resource "${id}" is not registered`);
    }

    // 创建新的音频元素实例以支持多音效叠加
    const audio = new Audio(resource.path);
    audio.preload = 'auto';
    return audio;
  }

  /**
   * 等待音频元素加载完成（用于确保在 Safari 上播放时没有延迟）
   * 对于音效，使用更短的超时时间，因为音效通常较短
   */
  private async waitForAudioReady(audio: HTMLAudioElement): Promise<void> {
    // 如果已经可以播放，直接返回
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      return;
    }

    // 等待 canplay 事件（至少可以播放当前帧）
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        // 超时后仍然尝试播放（可能是网络慢但文件存在）
        audio.removeEventListener('canplay', onCanPlay);
        audio.removeEventListener('error', onError);
        resolve();
      }, 2000); // 音效使用 2 秒超时（比音乐更短）

      const onCanPlay = () => {
        clearTimeout(timeout);
        audio.removeEventListener('canplay', onCanPlay);
        audio.removeEventListener('error', onError);
        resolve();
      };

      const onError = () => {
        clearTimeout(timeout);
        audio.removeEventListener('canplay', onCanPlay);
        audio.removeEventListener('error', onError);
        // 音效加载失败不抛出错误，静默处理
        resolve();
      };

      audio.addEventListener('canplay', onCanPlay, { once: true });
      audio.addEventListener('error', onError, { once: true });

      // 如果还没有开始加载，触发加载
      if (audio.readyState === HTMLMediaElement.HAVE_NOTHING) {
        audio.load();
      }
    });
  }

  /**
   * 播放音效（单次）
   */
  async playSound(id: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音效开关
    if (!this.audioManager.getSoundEffectEnabled()) {
      return;
    }

    try {
      const audio = this.createAudioElement(id);
      const volume = this.audioManager.calculateSoundEffectVolume(1.0, options.volume ?? 1.0);

      audio.volume = volume;
      audio.loop = options.loop ?? false;

      if (options.onEnded) {
        audio.addEventListener('ended', options.onEnded, { once: true });
      }

      if (options.onError) {
        audio.addEventListener('error', () => {
          options.onError?.(new Error(`Failed to play sound effect "${id}"`));
        }, { once: true });
      }

      // 播放结束后清理
      const cleanup = () => {
        try {
          audio.pause();
          audio.src = '';
          audio.load();
        } catch (e) {
          console.log(e);
        }
      };

      audio.addEventListener('ended', cleanup, { once: true });

      // 等待音频加载完成（减少 Safari 上的播放延迟）
      // 注意：对于音效，如果等待时间过长可能影响用户体验，所以使用较短的超时
      try {
        await this.waitForAudioReady(audio);
      } catch (error) {
        // 音效加载失败不阻止播放尝试
        console.warn(`Sound effect "${id}" may not be fully loaded, attempting to play anyway:`, error);
      }

      // 尝试播放（可能需要用户交互）
      try {
        await audio.play();
      } catch (error) {
        // 浏览器可能阻止自动播放
        console.warn('Sound effect autoplay was prevented:', error);
        // 清理资源
        cleanup();
      }
    } catch (error) {
      console.error(`Failed to play sound effect "${id}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 循环播放音效
   */
  async playLoopingSound(id: string, uniqueKey: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音效开关
    if (!this.audioManager.getSoundEffectEnabled()) {
      return;
    }

    // 如果已经存在相同 key 的循环音效，先停止
    if (this.loopingSounds.has(uniqueKey)) {
      this.stopLoopingSound(uniqueKey);
    }

    try {
      const audio = this.createAudioElement(id);
      const volume = this.audioManager.calculateSoundEffectVolume(1.0, options.volume ?? 1.0);

      audio.volume = volume;
      audio.loop = true;

      if (options.onError) {
        audio.addEventListener('error', () => {
          options.onError?.(new Error(`Failed to play looping sound effect "${id}"`));
        }, { once: true });
      }

      // 等待音频加载完成（减少 Safari 上的播放延迟）
      try {
        await this.waitForAudioReady(audio);
      } catch (error) {
        console.warn(`Looping sound effect "${id}" may not be fully loaded, attempting to play anyway:`, error);
      }

      // 尝试播放
      try {
        await audio.play();
        this.loopingSounds.set(uniqueKey, { audio, uniqueKey });
      } catch (error) {
        console.warn('Looping sound effect autoplay was prevented:', error);
        audio.src = '';
        audio.load();
      }
    } catch (error) {
      console.error(`Failed to play looping sound effect "${id}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 快速切换循环音效（用于转盘等需要频繁切换的场景）
   * 先启动新音效，再停止旧音效，减少切换间隙
   */
  async switchLoopingSound(newId: string, uniqueKey: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音效开关
    if (!this.audioManager.getSoundEffectEnabled()) {
      return;
    }

    // 如果当前有相同 key 的音效在播放，且是同一个音效，直接返回
    const currentSound = this.loopingSounds.get(uniqueKey);
    if (currentSound) {
      const resource = this.resources.get(newId);
      if (resource && currentSound.audio.src.endsWith(resource.path)) {
        return; // 已经是同一个音效，无需切换
      }
    }

    // 先创建并尝试播放新音效
    let newAudio: HTMLAudioElement | null = null;
    try {
      newAudio = this.createAudioElement(newId);
      const volume = this.audioManager.calculateSoundEffectVolume(1.0, options.volume ?? 1.0);

      newAudio.volume = volume;
      newAudio.loop = true;

      // 等待音频加载完成（减少 Safari 上的播放延迟）
      try {
        await this.waitForAudioReady(newAudio);
      } catch (error) {
        console.warn(`Switching to sound effect "${newId}" may not be fully loaded, attempting to play anyway:`, error);
      }

      // 尝试播放新音效
      try {
        await newAudio.play();

        // 新音效播放成功后，停止旧的（如果存在）
        if (currentSound) {
          try {
            currentSound.audio.pause();
            currentSound.audio.src = '';
            currentSound.audio.load();
          } catch (e) {
            // 忽略停止旧音效时的错误
            console.log(e);
          }
        }

        // 设置新音效
        this.loopingSounds.set(uniqueKey, { audio: newAudio, uniqueKey });
      } catch (error) {
        // 播放失败，清理新音效
        if (newAudio) {
          newAudio.src = '';
          newAudio.load();
        }
        console.warn('Failed to switch looping sound effect:', error);
      }
    } catch (error) {
      console.error(`Failed to switch looping sound effect "${newId}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 停止循环音效
   */
  stopLoopingSound(uniqueKey: string): void {
    const loopingSound = this.loopingSounds.get(uniqueKey);
    if (loopingSound) {
      loopingSound.audio.pause();
      loopingSound.audio.src = '';
      loopingSound.audio.load();
      this.loopingSounds.delete(uniqueKey);
    }
  }

  /**
   * 停止所有音效
   */
  stopAllSounds(): void {
    // 停止所有循环音效
    this.loopingSounds.forEach((loopingSound) => {
      loopingSound.audio.pause();
      loopingSound.audio.src = '';
      loopingSound.audio.load();
    });
    this.loopingSounds.clear();
  }

  /**
   * 更新所有循环音效音量（响应全局音量变化）
   */
  updateVolume(): void {
    const volume = this.audioManager.calculateSoundEffectVolume(1.0, 1.0);

    this.loopingSounds.forEach((loopingSound) => {
      loopingSound.audio.volume = volume;

      // 如果音效被关闭，暂停播放
      if (!this.audioManager.getSoundEffectEnabled() && !loopingSound.audio.paused) {
        loopingSound.audio.pause();
      }
    });
  }
}

